/* global PLICK_STAGE */
import { CRITICAL_EVENT_KEYS, EVENT_VALUE_TO_KEY } from './events';
import { clarityEvent, clarityIdentify, clarityInit, claritySetTags, clarityUpgrade } from './clarity';
import { ga4Event, ga4PageView, ga4SetUser, initGA4, setConsentDefault } from './ga4';
import { initMetaPixel } from './metaPixel';
import { flattenParams, hashEmail, toGa4EventName } from './sanitize';
import { EventParams, PostHogInstance, TelemetryProfile } from './types';

// Inicializa los sinks que no usan provider de React (Clarity + GA4 + Meta Pixel) y el
// consent default. PostHog se inicializa vía <PostHogProvider> en index.tsx.
export const initTelemetry = (opts: {
  clarityProjectId?: string;
  ga4MeasurementId?: string;
  metaPixelId?: string;
}): void => {
  setConsentDefault();
  if (opts.ga4MeasurementId) initGA4(opts.ga4MeasurementId);
  if (opts.metaPixelId) initMetaPixel(opts.metaPixelId);
  if (opts.clarityProjectId) clarityInit(opts.clarityProjectId);
};

// Dimensiones de negocio NO-PII compartidas por Clarity (tags) y GA4 (user_properties).
const businessDimensions = (profile: TelemetryProfile, userHash: string | null): EventParams => ({
  // Ambiente del build (`pruebas` | `prod`). Pruebas y prod comparten proyecto Clarity, así que
  // este tag es lo que permite filtrar/segmentar las stats por ambiente. No-PII, valor de build.
  stage: PLICK_STAGE,
  id_commerce: profile.idCommerce ?? undefined,
  id_path: profile.idPath ?? undefined,
  commerce_name: profile.commerceName ?? undefined,
  user_type: profile.userType ?? undefined,
  is_root: profile.isRoot,
  user_modules_count: profile.userModulesCount,
  onboarding_completed: profile.onboardingCompleted,
  plan_id: profile.planId ?? undefined,
  trial_days_remaining: profile.trialDaysRemaining ?? undefined,
  trial_used: profile.trialUsed ?? undefined,
  quota_usage_percent: profile.quotaUsagePercent ?? undefined,
  business_type: profile.businessType ?? undefined,
  business_sub_type: profile.businessSubType ?? undefined,
  country_code: profile.countryCode ?? undefined,
  timezone: profile.timezone ?? undefined,
  registered_at: profile.registeredAt ?? undefined,
  app_version: profile.appVersion ?? undefined,
  platform: profile.platform ?? undefined,
  desktop_app_version: profile.desktopAppVersion ?? undefined,
  user_hash: userHash ?? undefined,
});

// Identifica al usuario en los 3 sinks con el MISMO payload. Idempotente y re-llamable
// en cada sesión (resuelve el bug del gate que dejaba sesiones Clarity anónimas).
// - PostHog: payload completo con email/nombre en claro (preserva distinct_id = commerceName).
// - Clarity / GA4: dimensiones no-PII + user_hash (SHA-256 del email).
export const identify = async (posthog: PostHogInstance, profile: TelemetryProfile): Promise<void> => {
  if (!profile.idUser && !profile.commerceName) return;
  const userHash = await hashEmail(profile.email);

  posthog?.identify(profile.commerceName || profile.idUser, {
    id_commerce: profile.idCommerce,
    id_user: profile.idUser,
    id_path: profile.idPath,
    user_name: profile.userName,
    commerce_name: profile.commerceName,
    user_type: profile.userType,
    is_root: profile.isRoot,
    user_modules_count: profile.userModulesCount,
    onboarding_completed: profile.onboardingCompleted,
    plan_id: profile.planId,
    trial_days_remaining: profile.trialDaysRemaining,
    trial_used: profile.trialUsed,
    quota_usage_percent: profile.quotaUsagePercent,
    business_type: profile.businessType,
    business_sub_type: profile.businessSubType,
    country_code: profile.countryCode,
    timezone: profile.timezone,
    registered_at: profile.registeredAt,
    app_version: profile.appVersion,
    platform: profile.platform,
    desktop_app_version: profile.desktopAppVersion,
    user: {
      email: profile.email,
      name: profile.name,
      username: profile.userName,
      status: profile.userStatus,
    },
  });

  const dims = businessDimensions(profile, userHash);
  const customId = profile.idUser || profile.commerceName || '';
  clarityIdentify(customId, profile.commerceName);
  claritySetTags(dims);
  ga4SetUser(customId, dims);
};

// Espeja un evento de negocio a los 3 sinks. `eventName` es el display value (español).
export const trackEvent = (posthog: PostHogInstance, eventName: string, data?: Record<string, unknown>): void => {
  posthog?.capture(eventName, data);

  clarityEvent(eventName);
  const key = EVENT_VALUE_TO_KEY[eventName];
  if (key && CRITICAL_EVENT_KEYS.has(key)) clarityUpgrade(eventName);

  ga4Event(toGa4EventName(key ?? eventName), flattenParams(data));
};

// Pageview SPA: PostHog ($pageview) + GA4 (page_view manual).
export const pageView = (
  posthog: PostHogInstance,
  params: { page_path?: string; page_location?: string; page_title?: string },
): void => {
  posthog?.capture('$pageview');
  ga4PageView(params);
};

// Pageleave (solo PostHog; GA4 mide engagement automáticamente).
export const pageLeave = (posthog: PostHogInstance): void => {
  posthog?.capture('$pageleave');
};
